const rtlLanguageCodes = new Set(['ar', 'arc', 'ckb', 'dv', 'fa', 'he', 'ku', 'ps', 'sd', 'ug', 'ur', 'yi']);

function updateDocumentDirection() {
  const languageCode = document.documentElement.lang.toLowerCase().split('-')[0];
  document.documentElement.dir = rtlLanguageCodes.has(languageCode) ? 'rtl' : 'ltr';
}

updateDocumentDirection();

if ('MutationObserver' in window) {
  new MutationObserver(updateDocumentDirection).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
  });
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-package-form]');
  if (!form) return;
  const button = form.querySelector('button');
  if (!button || !window.fetch || !window.cartDrawer) return;
  event.preventDefault();
  window.cartDrawer.addFromForm(form, button);
});

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.panel = this.querySelector('.cart-drawer__panel');
    this.content = this.querySelector('[data-cart-drawer-content]');
    this.footer = this.querySelector('[data-cart-drawer-footer]');
    this.status = this.querySelector('[data-cart-drawer-status]');
    this.closeTimer = null;
    this.returnFocus = null;
    this.busy = false;
    this.destinationUrls = new Map();
    this.querySelectorAll('[data-cart-destination-url]').forEach((item) => {
      const destination = item.dataset.cartDestination;
      const url = item.dataset.cartDestinationUrl;
      if (destination && url) this.destinationUrls.set(destination, url);
    });
    this.bindEvents();
    window.cartDrawer = this;
    if (this.hasAttribute('data-clean-planned-dates')) this.refreshCart();
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    if (window.cartDrawer === this) window.cartDrawer = null;
  }

  bindEvents() {
    this.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-consent]')) this.updateConsent(event.target.checked);
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cart-link]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
      this.refreshCart();
    });
    document.addEventListener('keydown', (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  isOpen() {
    return !this.hidden && this.classList.contains('is-open');
  }

  async addFromForm(form, button) {
    if (this.busy) return;
    const variant = Number(form.querySelector('[name="id"]')?.value);
    if (!variant) return;
    const properties = {};
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^properties\[(.+)]$/);
      if (match && value !== '') properties[match[1]] = value;
    });
    this.setAddButtonLoading(button, true);
    this.setBusy(true, button);
    this.setStatus('');
    try {
      await this.request(`${window.routes.cart_add_url}.js`, { items: [{ id: variant, quantity: 1, properties }] });
      const cart = await this.getCart();
      this.render(cart);
      this.open(button);
      this.emitCartUpdate(cart);
    } catch (_) {
      this.open(button);
      this.setStatus(this.dataset.errorLabel);
    } finally {
      this.setAddButtonLoading(button, false);
      this.setBusy(false, button);
    }
  }

  async refreshCart() {
    if (this.busy) return;
    this.setBusy(true);
    this.setStatus('');
    try {
      const cart = await this.getCart();
      this.render(cart);
      this.emitCartUpdate(cart);
    } catch (_) {
      this.setStatus(this.dataset.errorLabel);
    } finally {
      this.setBusy(false);
    }
  }

  async changeQuantity(key, quantity) {
    if (!key || this.busy) return false;
    this.setBusy(true);
    this.setStatus('');
    try {
      const changeUrl = window.routes.cart_change_url || `${window.routes.cart_url}/change`;
      const cart = await this.request(`${changeUrl}.js`, { id: key, quantity: Math.max(0, quantity) });
      this.render(cart);
      this.emitCartUpdate(cart);
      return true;
    } catch (error) {
      this.setStatus(error.message || this.dataset.errorLabel);
      return false;
    } finally {
      this.setBusy(false);
    }
  }

  async updateConsent(accepted) {
    if (this.busy) return false;
    this.setBusy(true);
    this.setStatus('');
    try {
      const updateUrl = window.routes.cart_update_url || `${window.routes.cart_url}/update`;
      const cart = await this.request(`${updateUrl}.js`, {
        attributes: { [this.dataset.consentKey]: accepted ? this.consentTimestamp() : '' },
      });
      this.render(cart);
      this.emitCartUpdate(cart);
      return true;
    } catch (error) {
      const checkbox = this.querySelector('[data-cart-consent]');
      if (checkbox) checkbox.checked = !accepted;
      this.setStatus(error.message || this.dataset.errorLabel);
      return false;
    } finally {
      this.setBusy(false);
    }
  }

  async getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(this.dataset.errorLabel);
    let cart = await response.json();
    const dateKeys = [...new Set(['Start date', 'End date', this.dataset.startDateKey, this.dataset.endDateKey].filter(Boolean))];
    // Re-read returned lines after each change: removing properties can merge cart lines.
    const initialLineCount = cart.items.length;
    for (let index = 0; index < initialLineCount; index += 1) {
      const item = cart.items.find((line) => this.isDayPlan(line.properties, line.product_id)
        && dateKeys.some((key) => Object.hasOwn(line.properties || {}, key)));
      if (!item) break;
      const properties = { ...item.properties };
      dateKeys.forEach((key) => { delete properties[key]; });
      const changeUrl = window.routes.cart_change_url || `${window.routes.cart_url}/change`;
      cart = await this.request(`${changeUrl}.js`, { id: item.key, quantity: item.quantity, properties });
    }
    return cart;
  }

  async request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || this.dataset.errorLabel);
    }
    return response.json();
  }

  emitCartUpdate(cart) {
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    window.displayCurrency?.renderPrices(this);
  }

  setBusy(busy, sourceButton) {
    this.busy = busy;
    this.classList.toggle('is-busy', busy);
    this.panel?.setAttribute('aria-busy', String(busy));
    if (sourceButton) sourceButton.disabled = busy;
    this.querySelectorAll('[data-cart-action], [data-cart-consent]').forEach((control) => { control.disabled = busy; });
    const checkbox = this.querySelector('[data-cart-consent]');
    const checkout = this.querySelector('[data-cart-checkout]');
    if (checkout) checkout.disabled = busy || !checkbox?.checked;
  }

  setAddButtonLoading(button, loading) {
    if (!button?.matches('[data-cart-add-button]')) return;
    const label = button.querySelector('[data-cart-add-label]');
    const spinner = button.querySelector('[data-cart-add-spinner]');
    if (!button.dataset.originalLabel && label) button.dataset.originalLabel = label.textContent.trim();
    if (label) label.textContent = loading ? button.dataset.addingLabel : button.dataset.originalLabel;
    if (spinner) spinner.hidden = !loading;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
  }

  setStatus(message) {
    this.status.textContent = message || '';
  }

  open(trigger) {
    window.clearTimeout(this.closeTimer);
    if (trigger) this.returnFocus = trigger;
    if (this.isOpen()) return;
    this.hidden = false;
    document.body.classList.add('cy-cart-drawer--locked');
    this.setPageInert(true);
    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.querySelector('.cart-drawer__close')?.focus();
    });
  }

  close() {
    if (!this.isOpen()) return;
    this.classList.remove('is-open');
    document.body.classList.remove('cy-cart-drawer--locked');
    this.setPageInert(false);
    const finish = () => {
      if (!this.classList.contains('is-open')) this.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else {
      this.closeTimer = window.setTimeout(finish, 280);
    }
    this.returnFocus?.focus();
    this.returnFocus = null;
  }

  setPageInert(locked) {
    [document.getElementById('MainContent'), document.querySelector('footer'), document.querySelector('[data-header]')]
      .filter(Boolean)
      .forEach((element) => { element.inert = locked; });
  }

  trapFocus(event) {
    const candidates = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!candidates.length) return;
    const first = candidates[0];
    const last = candidates[candidates.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  consentTimestamp() {
    const values = new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    }).formatToParts(new Date()).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
    return `${values.weekday}, ${values.month} ${values.day}, ${values.year}, ${values.hour}:${values.minute}:${values.second} ${values.dayPeriod}`;
  }

  render(cart) {
    const items = cart.items || [];
    this.content.replaceChildren(items.length ? this.createItems(items) : this.createEmpty());
    this.footer.hidden = !items.length;
    if (!items.length) return;
    const totalDiscount = Number(cart.total_discount) || 0;
    const originalTotal = Number(cart.original_total_price);
    const subtotalAmount = totalDiscount > 0 && Number.isFinite(originalTotal)
      ? originalTotal
      : cart.total_price;
    const subtotal = this.footer.querySelector('.cart-drawer__subtotal strong');
    this.updatePrice(subtotal, subtotalAmount, cart.currency);
    const discountRow = this.footer.querySelector('[data-cart-drawer-discount]');
    const discountAmount = discountRow?.querySelector('[data-cart-drawer-discount-amount]');
    if (discountRow) discountRow.hidden = totalDiscount <= 0;
    this.updatePrice(discountAmount, totalDiscount, cart.currency);
    const totalRow = this.footer.querySelector('[data-cart-drawer-total]');
    if (totalRow) totalRow.hidden = totalDiscount <= 0;
    this.updatePrice(totalRow?.querySelector('strong'), cart.total_price, cart.currency);
    const checkbox = this.footer.querySelector('[data-cart-consent]');
    const checkout = this.footer.querySelector('[data-cart-checkout]');
    const accepted = Boolean(cart.attributes?.[this.dataset.consentKey]);
    checkbox.checked = accepted;
    checkbox.disabled = this.busy;
    checkout.disabled = !accepted || this.busy;
    window.displayCurrency?.renderPrices(this);
  }

  createItems(items) {
    const list = this.element('div', 'cart-drawer__items');
    const destinationImages = {};
    const hiddenDestinationImages = new Set();
    items.forEach((item) => {
      const properties = item.properties || {};
      if (properties._destination_image_hidden === 'true') {
        if (properties.Destination) hiddenDestinationImages.add(properties.Destination);
        return;
      }
      const image = properties._destination_image || item.featured_image?.url || item.image;
      if (properties.Destination && image && !destinationImages[properties.Destination]) {
        destinationImages[properties.Destination] = image;
      }
    });
    items.forEach((item) => list.append(this.createItem(item, destinationImages, hiddenDestinationImages)));
    return list;
  }

  isDayPlan(properties = {}, productId = null) {
    const existingDayPlan = productId && (this.dataset.dayPlanProductIds || '').trim().split(/\s+/).includes(String(productId));
    return properties?._plan_type === 'unlimited' || Boolean(existingDayPlan)
      || Boolean(this.dataset.dayPlanAllowanceLabel && properties?.['Data allowance'] === this.dataset.dayPlanAllowanceLabel);
  }

  createItem(item, destinationImages = {}, hiddenDestinationImages = new Set()) {
    const properties = item.properties || {};
    const destination = properties.Destination;
    const isTopup = Boolean(properties.eSIM);
    const title = item.product_title || item.title || '';
    const destinationUrl = this.destinationUrl(item);
    const article = this.element('article', 'cart-drawer__item');
    const hideDestinationImage = properties._destination_image_hidden === 'true'
      || Boolean(destination && hiddenDestinationImages.has(destination));
    const image = properties._destination_image || destinationImages[destination] || item.featured_image?.url || item.image;
    if (hideDestinationImage) {
      article.classList.add('cart-drawer__item--no-image');
    } else if (image) {
      const element = this.element('img', 'cart-drawer__image');
      element.src = image;
      element.width = 72;
      element.height = 72;
      element.alt = '';
      if (isTopup) {
        const staticImage = this.element('span', 'cart-drawer__image-link');
        staticImage.append(element);
        article.append(staticImage);
      } else {
        article.append(this.destinationLink(destinationUrl, destination || title, 'cart-drawer__image-link', element));
      }
    } else {
      const placeholder = this.element('span', 'cart-drawer__image cart-drawer__image--placeholder');
      placeholder.append(this.globeIcon());
      if (isTopup) {
        const staticImage = this.element('span', 'cart-drawer__image-link');
        staticImage.append(placeholder);
        article.append(staticImage);
      } else {
        article.append(this.destinationLink(destinationUrl, destination || title, 'cart-drawer__image-link', placeholder));
      }
    }
    const details = this.element('div', 'cart-drawer__item-details');
    if (destination) details.append(this.element('p', 'cart-drawer__destination', destination));
    const heading = this.element('h3');
    if (isTopup) heading.textContent = title;
    else heading.append(this.destinationLink(destinationUrl, title, 'cart-drawer__item-link', title));
    details.append(heading);
    const meta = this.element('dl', 'cart-drawer__meta');
    this.addMeta(meta, this.dataset.esimSerialLabel, properties.eSIM);
    this.addMeta(meta, this.dataset.dataLabel, properties['Data allowance']);
    const dayPlan = this.isDayPlan(properties, item.product_id);
    const days = Number(properties._validity_days || Number.parseInt(properties.Duration, 10));
    const knownDuration = dayPlan && Number.isInteger(days) && days >= 1 && days <= 90;
    let duration = properties.Duration;
    let validity = this.dataset.dayPlanValidityFallback;
    if (knownDuration) {
      duration ||= (days === 1 ? this.dataset.dayTemplate : this.dataset.daysTemplate).replace('__days__', days);
      if (days <= 3) {
        validity = this.dataset.dayPlanValidityTemplate.replace('__hours__', days * 24);
        duration = this.dataset.dayPlanDurationTemplate.replace('__duration__', duration).replace('__hours__', days * 24);
      } else {
        validity = this.dataset.dayPlanValidityDaysTemplate.replace('__duration__', duration);
      }
    }
    this.addMeta(meta, this.dataset.durationLabel, duration);
    if (dayPlan) {
      this.addMeta(meta, this.dataset.startsLabel, this.dataset.dayPlanStartsLabel);
      this.addMeta(meta, this.dataset.validityLabel, validity);
    }
    if (meta.childElementCount) details.append(meta);
    if (dayPlan) details.append(this.element('p', 'cart-plan__validity-note', this.dataset.dayPlanDayLengthLabel));
    const prices = this.element('div', 'cart-drawer__prices');
    if (Number(item.original_line_price) > Number(item.final_line_price)) {
      prices.append(
        this.element('span', 'visually-hidden', `${this.dataset.originalPriceLabel}:`),
        this.priceElement('s', 'cart-drawer__original-price', item.original_line_price, item.currency),
        this.element('span', 'visually-hidden', `${this.dataset.discountedPriceLabel}:`),
      );
    }
    prices.append(this.priceElement('span', 'cart-drawer__price', item.final_line_price, item.currency));
    details.append(prices);
    const discounts = this.createDiscountList(item.line_level_discount_allocations, item.currency);
    if (discounts) details.append(discounts);
    article.append(details);
    const actions = this.element('div', 'cart-drawer__item-actions');
    const quantity = this.element('div', 'cart-drawer__quantity');
    quantity.setAttribute('role', 'group');
    quantity.setAttribute('aria-label', `${this.dataset.quantityLabel} ${title}`);
    quantity.append(this.actionButton('decrease', item, '−', this.dataset.decreaseLabel.replace('__title__', title)));
    quantity.append(this.element('span', 'cart-drawer__quantity-value', item.quantity));
    quantity.append(this.actionButton('increase', item, '+', this.dataset.increaseLabel.replace('__title__', title)));
    actions.append(quantity, this.actionButton('remove', item, '⌫', this.dataset.removeLabel));
    article.append(actions);
    return article;
  }

  destinationUrl(item) {
    const properties = item.properties || {};
    const destination = properties.Destination;
    const candidate = properties._destination_url || this.destinationUrls.get(destination) || item.url || window.routes.cart_url;
    try {
      const url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid destination URL');
      if (destination && properties._destination_url) this.destinationUrls.set(destination, url.href);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return window.routes.cart_url;
    }
  }

  destinationLink(url, label, className, content) {
    const link = this.element('a', className);
    link.href = url;
    link.setAttribute('aria-label', label);
    if (content instanceof Node) link.append(content);
    else link.textContent = content;
    return link;
  }

  addMeta(list, label, value) {
    if (!value) return;
    const group = this.element('div');
    group.append(this.element('dt', '', label), this.element('dd', '', value));
    list.append(group);
  }

  createDiscountList(allocations, currency) {
    if (!Array.isArray(allocations) || !allocations.length) return null;
    const list = this.element('ul', 'cart-drawer__discounts');
    list.setAttribute('aria-label', this.dataset.discountsLabel);
    allocations.forEach((allocation) => {
      const item = this.element('li');
      const title = allocation.discount_application?.title || this.dataset.discountLabel;
      const label = this.element('span', 'cart-drawer__discount-label');
      label.append(this.sealPercentIcon(), this.element('span', '', title));
      const amount = this.element('strong');
      const minus = this.element('span', '', '−');
      minus.setAttribute('aria-hidden', 'true');
      amount.append(minus, this.priceElement('span', '', allocation.amount, currency));
      item.append(label, amount);
      list.append(item);
    });
    return list;
  }

  actionButton(action, item, text, label) {
    const button = this.element('button', action === 'remove' ? 'cart-drawer__remove' : 'cart-drawer__quantity-button', text);
    button.type = 'button';
    button.dataset.cartAction = action;
    button.dataset.cartKey = item.key || item.id;
    button.dataset.cartQuantity = item.quantity;
    button.setAttribute('data-cart-action', '');
    button.setAttribute('aria-label', label);
    if (action === 'remove') button.replaceChildren(this.trashIcon());
    button.disabled = this.busy;
    button.addEventListener('click', () => {
      if (this.busy) return;
      const key = button.dataset.cartKey;
      const quantity = Number(button.dataset.cartQuantity);
      if (action === 'remove') this.changeQuantity(key, 0);
      if (action === 'decrease') this.changeQuantity(key, quantity - 1);
      if (action === 'increase') this.changeQuantity(key, quantity + 1);
    });
    return button;
  }

  createEmpty() {
    const empty = this.element('div', 'cart-drawer__empty');
    const link = this.element('a', 'button', this.dataset.continueLabel);
    link.href = this.dataset.homeUrl;
    empty.append(this.element('h3', '', this.dataset.emptyHeading), this.element('p', '', this.dataset.emptyCopy), link);
    return empty;
  }

  trashIcon() {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', 'M5.75 7.5h12.5M9.25 7.5V5.75h5.5V7.5M8 10.25v6.25M12 10.25v6.25M16 10.25v6.25M7 7.5l.7 11.25h8.6L17 7.5');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    icon.append(path);
    return icon;
  }

  globeIcon() {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    const circle = document.createElementNS(namespace, 'circle');
    circle.setAttribute('cx', '12'); circle.setAttribute('cy', '12'); circle.setAttribute('r', '8.25');
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', 'M3.9 12h16.2M12 3.75c2.05 2.24 3.12 5 3.12 8.25S14.05 18.01 12 20.25C9.95 18.01 8.88 15.25 8.88 12S9.95 5.99 12 3.75Z');
    [circle, path].forEach((element) => {
      element.setAttribute('fill', 'none'); element.setAttribute('stroke', 'currentColor'); element.setAttribute('stroke-width', '1.8'); element.setAttribute('stroke-linecap', 'round'); element.setAttribute('stroke-linejoin', 'round');
    });
    icon.append(circle, path);
    return icon;
  }

  sealPercentIcon() {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('class', 'cart-drawer__discount-icon');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', 'M225.86,102.82c-3.77-3.94-7.67-8-9.14-11.57-1.36-3.27-1.44-8.69-1.52-13.94-.15-9.76-.31-20.82-8-28.51s-18.75-7.85-28.51-8c-5.25-.08-10.67-.16-13.94-1.52-3.56-1.47-7.63-5.37-11.57-9.14C146.28,23.51,138.44,16,128,16s-18.27,7.51-25.18,14.14c-3.94,3.77-8,7.67-11.57,9.14C88,40.64,82.56,40.72,77.31,40.8c-9.76.15-20.82.31-28.51,8S41,67.55,40.8,77.31c-.08,5.25-.16,10.67-1.52,13.94-1.47,3.56-5.37,7.63-9.14,11.57C23.51,109.73,16,117.56,16,128s7.51,18.27,14.14,25.18c3.77,3.94,7.67,8,9.14,11.57,1.36,3.27,1.44,8.69,1.52,13.94.15,9.76.31,20.82,8,28.51s18.75,7.85,28.51,8c5.25.08,10.67.16,13.94,1.52,3.56,1.47,7.63,5.37,11.57,9.14C109.72,232.49,117.56,240,128,240s18.27-7.51,25.18-14.14c3.94-3.77,8-7.67,11.57-9.14,3.27-1.36,8.69-1.44,13.94-1.52,9.76-.15,20.82-.31,28.51-8s7.85-18.75,8-28.51c.08-5.25.16-10.67,1.52-13.94,1.47-3.56,5.37-7.63,9.14-11.57C232.49,146.27,240,138.44,240,128S232.49,109.73,225.86,102.82Zm-11.55,39.29c-4.79,5-9.75,10.17-12.38,16.52-2.52,6.1-2.63,13.07-2.73,19.82-.1,7-.21,14.33-3.32,17.43s-10.39,3.22-17.43,3.32c-6.75.1-13.72.21-19.82,2.73-6.35,2.63-11.52,7.59-16.52,12.38S132,224,128,224s-9.15-4.92-14.11-9.69-10.17-9.75-16.52-12.38c-6.1-2.52-13.07-2.63-19.82-2.73-7-.1-14.33-.21-17.43-3.32s-3.22-10.39-3.32-17.43c-.1-6.75-.21-13.72-2.73-19.82-2.63-6.35-7.59-11.52-12.38-16.52S32,132,32,128s4.92-9.14,9.69-14.11,9.75-10.17,12.38-16.52c2.52-6.1,2.63-13.07,2.73-19.82.1-7,.21-14.33,3.32-17.43S70.51,56.9,77.55,56.8c6.75-.1,13.72-.21,19.82-2.73,6.35-2.63,11.52-7.59,16.52-12.38S124,32,128,32s9.15,4.92,14.11,9.69,10.17,9.75,16.52,12.38c6.1,2.52,13.07,2.63,19.82,2.73,7,.1,14.33.21,17.43,3.32s3.22,10.39,3.32,17.43c.1,6.75.21,13.72,2.73,19.82,2.63,6.35,7.59,11.52,12.38,16.52S224,124,224,128,219.08,137.14,214.31,142.11ZM120,96a24,24,0,1,0-24,24A24,24,0,0,0,120,96ZM88,96a8,8,0,1,1,8,8A8,8,0,0,1,88,96Zm72,40a24,24,0,1,0,24,24A24,24,0,0,0,160,136Zm0,32a8,8,0,1,1,8-8A8,8,0,0,1,160,168Zm13.66-74.34-80,80a8,8,0,0,1-11.32-11.32l80-80a8,8,0,0,1,11.32,11.32Z');
    path.setAttribute('transform', 'scale(.09375)');
    path.setAttribute('fill', 'currentColor');
    icon.append(path);
    return icon;
  }

  element(tag, className = '', content = '') {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== '') element.textContent = content;
    return element;
  }

  priceElement(tag, className, cents, currency) {
    const price = this.element(tag, className, this.formatPrice(cents, currency));
    price.dataset.priceUsdCents = cents;
    price.setAttribute('data-display-price-with-usd', '');
    return price;
  }

  updatePrice(element, cents, currency) {
    if (!element) return;
    element.dataset.priceUsdCents = cents;
    element.textContent = this.formatPrice(cents, currency);
  }

  formatPrice(cents, currency) {
    return window.displayCurrency?.formatCurrency(cents) || new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(Number(cents || 0) / 100);
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartPage extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    this.content = this.querySelector('[data-cart-page-content]');
    this.status = this.querySelector('[data-cart-page-status]');
    this.refreshPromise = null;
    this.busy = false;
    this.listen(this, 'click', (event) => this.handleAction(event));
    this.listen(this, 'change', (event) => this.handleConsent(event));
    this.listen(window, 'cart:updated', () => this.refresh());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.initialized = false;
  }

  listen(target, eventName, handler) {
    target.addEventListener(eventName, handler, { signal: this.abortController.signal });
  }

  async handleAction(event) {
    const button = event.target.closest('[data-cart-page-action]');
    if (!button || this.busy) return;
    const { cartKey: key, cartQuantity: quantity, cartPageAction: action } = button.dataset;
    if (!key) return;
    const currentQuantity = Number(quantity);
    const nextQuantity = action === 'remove' ? 0 : currentQuantity + (action === 'increase' ? 1 : -1);
    await this.mutate(() => window.cartDrawer?.changeQuantity(key, nextQuantity));
  }

  async handleConsent(event) {
    const checkbox = event.target.closest('[data-cart-page-consent]');
    if (!checkbox || this.busy) return;
    await this.mutate(() => window.cartDrawer?.updateConsent(checkbox.checked));
  }

  async mutate(operation) {
    if (!window.cartDrawer) {
      this.setStatus(this.dataset.errorLabel);
      return;
    }
    this.setBusy(true);
    this.setStatus(this.dataset.updatingLabel);
    try {
      const updated = await operation();
      const refreshed = await this.refresh();
      this.setStatus(updated && refreshed ? '' : this.dataset.errorLabel);
    } finally {
      this.setBusy(false);
    }
  }

  async refresh() {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(this.dataset.cartUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(this.dataset.errorLabel);
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextContent = documentFragment.querySelector('[data-cart-page-content]');
        if (!nextContent || !this.content) throw new Error(this.dataset.errorLabel);
        this.content.replaceWith(nextContent);
        this.content = nextContent;
        window.displayCurrency?.renderPrices(this);
        window.displayCurrency?.renderCurrencyLabels();
        return true;
      } catch (_) {
        this.setStatus(this.dataset.errorLabel);
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[data-cart-page-action], [data-cart-page-consent]').forEach((control) => { control.disabled = busy; });
    const checkout = this.querySelector('[data-cart-page-checkout]');
    const consent = this.querySelector('[data-cart-page-consent]');
    if (checkout) checkout.disabled = busy || !consent?.checked;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message || '';
  }
}

if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);

class ConnectedYouHeader extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.mobileNavigation = this.querySelector('[data-header-mobile-nav]');
    this.mobileNavigationTrigger = this.querySelector('[data-header-toggle-nav]');
    this.mobileDisclosures = [...this.querySelectorAll('[data-header-mobile-disclosure]')];
    this.menus = [...this.querySelectorAll('[data-header-menu]')];
    this.dialogs = [...this.querySelectorAll('[data-header-dialog]')];
    this.desktopNavigation = this.querySelector('.cy-header__desktop-nav');
    this.desktopNavigationList = this.querySelector('.cy-header__desktop-list');
    this.desktopNavigationMedia = window.matchMedia('(min-width: 1020px)');
    this.mobileNavigationCloseTimer = null;

    this.bindMenus();
    this.bindMobileNavigation();
    this.bindDialogs();
    this.bindAppearanceMenu();
    this.bindCartCount();
    this.bindDesktopNavigationFit();
    this.listen(window, 'sticky-search:available', () => this.bindStickySearch());
    this.bindStickySearch();
    window.displayCurrency?.renderCurrencyOptions();
    window.displayCurrency?.renderCurrencyLabels();
  }

  disconnectedCallback() {
    window.clearTimeout(this.mobileNavigationCloseTimer);
    window.cancelAnimationFrame(this.stickySearchFrame);
    window.cancelAnimationFrame(this.navigationFitFrame);
    this.navigationResizeObserver?.disconnect();
    this.navigationMutationObserver?.disconnect();
    this.setStickySearchDocked(false);
    this.abortController?.abort();
    this.setPageInert(false);
    document.body.classList.remove('cy-header--locked');
    this.initialized = false;
  }

  listen(target, eventName, handler, options = {}) {
    target?.addEventListener(eventName, handler, {
      ...options,
      signal: this.abortController.signal,
    });
  }

  closeMenus(except) {
    this.menus.forEach((menu) => {
      if (menu !== except) menu.removeAttribute('open');
      const summary = menu.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', menu === except && menu.open ? 'true' : 'false');
    });
  }

  bindMenus() {
    this.menus.forEach((menu) => {
      const summary = menu.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      this.listen(menu, 'toggle', () => {
        if (menu.open) {
          this.closeMenus(menu);
          this.closeMobileNavigation({ restoreFocus: false, immediate: true });
        }
        if (summary) summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
      });
    });

    this.listen(document, 'pointerdown', (event) => {
      if (!this.contains(event.target)) this.closeMenus();
    });
    this.listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && !this.dialogs.some((dialog) => dialog.open) && !this.isMobileNavigationOpen()) {
        const openMenu = this.menus.find((menu) => menu.open);
        this.closeMenus();
        openMenu?.querySelector('summary')?.focus();
      }
    });
  }

  isMobileNavigationOpen() {
    return Boolean(this.mobileNavigation && !this.mobileNavigation.hidden);
  }

  isDesktopStickyNavigation() {
    return Boolean(
      (this.stickySearchDocked && this.stickySearchDesktopMedia?.matches)
      || (this.hasAttribute('data-navigation-overflow') && this.desktopNavigationMedia?.matches),
    );
  }

  bindDesktopNavigationFit() {
    if (!this.desktopNavigation || !this.desktopNavigationList) return;

    this.queueDesktopNavigationFit();
    if ('ResizeObserver' in window) {
      this.navigationResizeObserver = new ResizeObserver(() => this.queueDesktopNavigationFit());
      this.navigationResizeObserver.observe(this);
    }
    if ('MutationObserver' in window) {
      this.navigationMutationObserver = new MutationObserver(() => this.queueDesktopNavigationFit());
      this.navigationMutationObserver.observe(this.desktopNavigationList, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
    this.listen(window, 'resize', () => this.queueDesktopNavigationFit(), { passive: true });
    this.listen(this.desktopNavigationMedia, 'change', () => this.queueDesktopNavigationFit());
  }

  queueDesktopNavigationFit() {
    if (this.navigationFitFrame) return;
    this.navigationFitFrame = window.requestAnimationFrame(() => {
      this.navigationFitFrame = 0;
      this.updateDesktopNavigationFit();
    });
  }

  updateDesktopNavigationFit() {
    if (!this.desktopNavigation || !this.desktopNavigationList) return;

    const hadOverflow = this.hasAttribute('data-navigation-overflow');
    if (hadOverflow) this.removeAttribute('data-navigation-overflow');

    const hasOverflow = this.desktopNavigationMedia.matches
      && this.desktopNavigationList.scrollWidth > this.desktopNavigation.clientWidth + 1;

    if (hasOverflow) {
      this.setAttribute('data-navigation-overflow', 'true');
    } else if (hadOverflow && this.isMobileNavigationOpen()) {
      this.closeMobileNavigation({ restoreFocus: false, immediate: true });
    }
  }

  openMobileNavigation() {
    if (!this.mobileNavigation || !this.mobileNavigationTrigger || this.isMobileNavigationOpen()) return;
    window.clearTimeout(this.mobileNavigationCloseTimer);
    this.closeMenus();
    this.stickySearch?.close?.();
    this.mobileNavigation.hidden = false;
    this.mobileNavigation.classList.remove('is-closing');
    window.requestAnimationFrame(() => this.mobileNavigation.classList.add('is-open'));
    this.mobileNavigationTrigger.setAttribute('aria-expanded', 'true');
    this.mobileNavigationTrigger.setAttribute('aria-label', this.mobileNavigationTrigger.dataset.closeLabel);
    if (!this.isDesktopStickyNavigation()) {
      document.body.classList.add('cy-header--locked');
      this.setPageInert(true);
    }

    window.requestAnimationFrame(() => {
      this.mobileNavigation.querySelector('a, summary, button')?.focus();
    });
  }

  closeMobileNavigation({ restoreFocus = true, immediate = false } = {}) {
    if (!this.mobileNavigation || !this.mobileNavigationTrigger || !this.isMobileNavigationOpen()) return;
    window.clearTimeout(this.mobileNavigationCloseTimer);
    this.mobileNavigationTrigger.setAttribute('aria-expanded', 'false');
    this.mobileNavigationTrigger.setAttribute('aria-label', this.mobileNavigationTrigger.dataset.openLabel);
    document.body.classList.remove('cy-header--locked');
    this.setPageInert(false);
    this.mobileNavigation.classList.remove('is-open');

    const finishClosing = () => {
      if (!this.mobileNavigation.classList.contains('is-closing') && !immediate) return;
      this.mobileNavigation.classList.remove('is-closing');
      this.mobileNavigation.hidden = true;
      this.mobileDisclosures.forEach((disclosure) => {
        disclosure.removeAttribute('open');
        disclosure.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        const reveal = disclosure.querySelector('.cy-header__mobile-submenu-reveal');
        if (reveal) reveal.inert = true;
      });
    };

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.mobileNavigation.classList.add('is-closing');
      finishClosing();
    } else {
      this.mobileNavigation.classList.add('is-closing');
      this.listen(this.mobileNavigation, 'animationend', finishClosing, { once: true });
      this.mobileNavigationCloseTimer = window.setTimeout(finishClosing, 220);
    }

    if (restoreFocus) this.mobileNavigationTrigger.focus();
  }

  bindMobileNavigation() {
    if (!this.mobileNavigation || !this.mobileNavigationTrigger) return;
    this.listen(this.mobileNavigationTrigger, 'click', () => {
      if (this.isMobileNavigationOpen()) {
        this.closeMobileNavigation();
      } else {
        this.openMobileNavigation();
      }
    });
    this.listen(document, 'pointerdown', (event) => {
      if (this.isMobileNavigationOpen() && !this.contains(event.target)) this.closeMobileNavigation();
    });
    this.listen(document, 'keydown', (event) => {
      if (
        event.key === 'Escape' &&
        this.isMobileNavigationOpen() &&
        !this.dialogs.some((dialog) => dialog.open)
      ) {
        this.closeMobileNavigation();
        return;
      }

      if (
        event.key === 'Tab' &&
        this.isMobileNavigationOpen() &&
        !this.isDesktopStickyNavigation() &&
        !this.dialogs.some((dialog) => dialog.open)
      ) {
        this.trapMobileFocus(event);
      }
    });
    this.mobileNavigation.querySelectorAll('a').forEach((link) => {
      this.listen(link, 'click', () => this.closeMobileNavigation({ restoreFocus: false, immediate: true }));
    });
    this.mobileDisclosures.forEach((disclosure) => {
      const summary = disclosure.querySelector('summary');
      const reveal = disclosure.querySelector('.cy-header__mobile-submenu-reveal');
      this.listen(disclosure, 'toggle', () => {
        if (summary) summary.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
        if (reveal) reveal.inert = !disclosure.open;
      });
    });
    this.listen(this.desktopNavigationMedia, 'change', (event) => {
      if (event.matches && !this.hasAttribute('data-navigation-overflow')) {
        this.closeMobileNavigation({ restoreFocus: false, immediate: true });
      }
    });
  }

  setPageInert(locked) {
    const targets = [document.getElementById('MainContent'), document.querySelector('footer')].filter(Boolean);
    targets.forEach((target) => {
      if (!this.contains(target)) target.inert = locked;
    });
  }

  trapMobileFocus(event) {
    const candidates = [
      this.mobileNavigationTrigger,
      ...this.mobileNavigation.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'),
    ].filter((element) => !element.hidden && element.getClientRects().length > 0);

    if (!candidates.length) return;
    const first = candidates[0];
    const last = candidates[candidates.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!candidates.includes(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  openDialog(name, trigger) {
    const dialog = this.dialogs.find((item) => item.dataset.headerDialog === name);
    if (!dialog) return;
    this.closeMenus();
    const openedFromMobileNavigation = this.mobileNavigation && this.mobileNavigation.contains(trigger);
    if (!openedFromMobileNavigation) {
      this.closeMobileNavigation({ restoreFocus: false, immediate: true });
    }
    if (!dialog.open) {
      dialog.__returnFocus = trigger;
      dialog.showModal();
    }
  }

  bindDialogs() {
    this.querySelectorAll('[data-header-open-dialog]').forEach((button) => {
      this.listen(button, 'click', () => this.openDialog(button.dataset.headerOpenDialog, button));
    });
  }

  bindAppearanceMenu() {
    const options = [...this.querySelectorAll('[data-theme-option]')];
    if (!options.length) return;

    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
    const mobileThemeDefault = window.matchMedia('(max-width: 1019px)');
    const supportedThemes = new Set(['light', 'dark', 'system']);
    let preference = mobileThemeDefault.matches ? 'system' : 'light';
    let hasSavedPreference = false;

    try {
      const savedTheme = window.localStorage.getItem('cy-theme');
      if (supportedThemes.has(savedTheme)) {
        preference = savedTheme;
        hasSavedPreference = true;
      }
    } catch (_) {
      // Theme selection remains functional when storage is unavailable.
    }

    const update = () => {
      const resolvedTheme = preference === 'system' && systemTheme.matches ? 'dark' : preference === 'system' ? 'light' : preference;
      document.documentElement.dataset.theme = resolvedTheme;
      document.documentElement.dataset.themePreference = preference;
      options.forEach((option) => {
        option.setAttribute('aria-pressed', String(option.dataset.themeOption === preference));
      });
    };

    update();

    options.forEach((option) => {
      this.listen(option, 'click', () => {
        preference = option.dataset.themeOption;
        hasSavedPreference = true;
        try {
          window.localStorage.setItem('cy-theme', preference);
        } catch (_) {
          // The selected theme still applies for the current page.
        }
        update();
        const menu = option.closest('[data-appearance-menu]');
        menu?.removeAttribute('open');
        menu?.querySelector('summary')?.focus();
      });
    });

    this.listen(systemTheme, 'change', () => {
      if (preference === 'system') update();
    });

    this.listen(mobileThemeDefault, 'change', () => {
      if (hasSavedPreference) return;
      preference = mobileThemeDefault.matches ? 'system' : 'light';
      update();
    });
  }

  bindCartCount() {
    this.listen(window, 'cart:updated', (event) => {
      const count = Number(event.detail?.item_count ?? event.detail?.cart?.item_count);
      if (!Number.isFinite(count)) return;
      this.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count === 0;
      });
      this.querySelectorAll('[data-cart-link]').forEach((link) => {
        link.setAttribute('aria-label', count > 0 ? `${link.dataset.cartLabel} (${count})` : link.dataset.cartLabel);
      });
    });
  }

  bindStickySearch() {
    if (this.stickySearchBound) return;

    const stickySearchOrigin = document.querySelector('[data-sticky-search-origin]');
    const stickySearch = stickySearchOrigin?.querySelector('[data-sticky-search-content]');
    const stickySearchDocks = [...this.querySelectorAll('[data-header-search-dock]')];
    const allowInDesignMode = stickySearchOrigin?.hasAttribute('data-sticky-search-in-design-mode');
    if (!stickySearch || !stickySearchDocks.length || (window.Shopify?.designMode && !allowInDesignMode)) return;

    this.stickySearchOrigin = stickySearchOrigin;
    this.stickySearch = stickySearch;
    this.stickySearchDocks = stickySearchDocks;
    this.stickySearchBound = true;
    this.stickySearchDocked = false;
    this.stickySearchFrame = 0;
    this.stickySearchDesktopMedia = window.matchMedia('(min-width: 1020px)');
    this.listen(this.stickySearch, 'focusin', () => {
      this.closeMobileNavigation({ restoreFocus: false, immediate: true });
    });
    this.listen(window, 'scroll', () => this.queueStickySearchUpdate(), { passive: true });
    this.listen(window, 'resize', () => this.queueStickySearchUpdate(), { passive: true });
    this.listen(this.stickySearchDesktopMedia, 'change', () => {
      if (this.stickySearchDocked) {
        const dock = this.activeStickySearchDock();
        this.stickySearchDocks.forEach((item) => { item.hidden = item !== dock; });
        this.moveStickySearch(dock);
      }
      this.queueStickySearchUpdate();
    });
    this.queueStickySearchUpdate();
  }

  activeStickySearchDock() {
    const mode = this.stickySearchDesktopMedia?.matches ? 'desktop' : 'mobile';
    return this.stickySearchDocks?.find((dock) => dock.dataset.headerSearchDock === mode);
  }

  queueStickySearchUpdate() {
    if (!this.stickySearch || this.stickySearchFrame) return;
    this.stickySearchFrame = window.requestAnimationFrame(() => {
      this.stickySearchFrame = 0;
      this.updateStickySearch();
    });
  }

  updateStickySearch() {
    if (!this.stickySearchOrigin?.isConnected || !this.stickySearch?.isConnected) return;

    const originStyle = window.getComputedStyle(this.stickySearchOrigin);
    if (!this.stickySearchOrigin.getClientRects().length || originStyle.visibility === 'hidden') {
      this.setStickySearchDocked(false);
      return;
    }

    const originRect = this.stickySearchOrigin.getBoundingClientRect();
    const headerBottom = this.querySelector('.cy-header__inner')?.getBoundingClientRect().bottom || 0;
    const shouldDock = this.stickySearchDocked
      ? originRect.top < headerBottom + 8
      : originRect.bottom <= headerBottom;
    this.setStickySearchDocked(shouldDock);
  }

  setStickySearchDocked(docked) {
    if (!this.stickySearch || !this.stickySearchOrigin || docked === this.stickySearchDocked) return;

    if (docked) {
      const dock = this.activeStickySearchDock();
      if (!dock) return;
      const originHeight = this.stickySearchOrigin.getBoundingClientRect().height;
      this.stickySearchOrigin.style.minBlockSize = `${originHeight}px`;
      this.stickySearchDocks.forEach((item) => { item.hidden = item !== dock; });
      this.moveStickySearch(dock);
      this.classList.add('cy-header--search-docked');
      this.stickySearchDocked = true;
      return;
    }

    if (this.stickySearchDesktopMedia?.matches) {
      this.closeMobileNavigation({ restoreFocus: false, immediate: true });
    }
    this.moveStickySearch(this.stickySearchOrigin);
    this.stickySearchOrigin.style.removeProperty('min-block-size');
    this.stickySearchDocks?.forEach((dock) => { dock.hidden = true; });
    this.classList.remove('cy-header--search-docked');
    this.stickySearchDocked = false;
  }

  moveStickySearch(target) {
    if (!target || this.stickySearch.parentElement === target) return;
    const input = this.stickySearch.querySelector('input');
    const restoreFocus = input === document.activeElement;
    const selectionStart = restoreFocus ? input.selectionStart : null;
    const selectionEnd = restoreFocus ? input.selectionEnd : null;

    target.hidden = false;
    target.append(this.stickySearch);

    if (restoreFocus) {
      window.requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
        if (selectionStart !== null && selectionEnd !== null) input.setSelectionRange(selectionStart, selectionEnd);
      });
    }
  }
}

if (!customElements.get('connected-you-header')) {
  customElements.define('connected-you-header', ConnectedYouHeader);
}

function initializeModals(root = document) {
  const dialogs = [];
  if (root.matches?.('[data-modal]')) dialogs.push(root);
  if (root.querySelectorAll) dialogs.push(...root.querySelectorAll('[data-modal]'));

  dialogs.forEach((dialog) => {
    if (dialog.dataset.modalInitialized === 'true') return;
    dialog.dataset.modalInitialized = 'true';

    dialog.querySelectorAll('[data-modal-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      dialog.close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      dialog.__returnFocus?.focus();
      dialog.__returnFocus = null;
    });
  });
}

function initializePartnershipInquiry(root = document) {
  const inquiries = [];
  if (root.matches?.('[data-partnership-inquiry]')) inquiries.push(root);
  if (root.querySelectorAll) inquiries.push(...root.querySelectorAll('[data-partnership-inquiry]'));

  inquiries.forEach((inquiry) => {
    const dialog = inquiry.querySelector('[data-partnership-dialog]');
    const form = inquiry.querySelector('form');
    if (!dialog || !form) return;
    const openButtons = [...document.querySelectorAll(`[data-partnership-open="${dialog.id}"]`)];

    if (inquiry.dataset.partnershipInitialized === 'true') {
      openButtons.forEach((button) => {
        if (button.dataset.partnershipTriggerInitialized === 'true') return;
        button.dataset.partnershipTriggerInitialized = 'true';
        button.addEventListener('click', () => dialog.__openPartnershipInquiry?.(button));
      });
      return;
    }

    inquiry.dataset.partnershipInitialized = 'true';
    const submitButton = form.querySelector('[data-partnership-submit]');
    const storageKey = `cy-partnership-inquiry-${form.id}`;

    const saveValues = () => {
      try {
        const values = {};
        [...form.elements].forEach((field) => {
          if (!field.name || field.type === 'hidden' || field.type === 'submit') return;
          values[field.name] = field.value;
        });
        window.sessionStorage.setItem(storageKey, JSON.stringify(values));
      } catch (_) {
        // Form submission remains available when session storage is unavailable.
      }
    };

    const restoreValues = () => {
      if (!dialog.hasAttribute('data-partnership-restore-form')) return;
      try {
        const values = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        Object.entries(values).forEach(([name, value]) => {
          const field = form.elements.namedItem(name);
          if (field && typeof field.value === 'string' && !field.value) field.value = value;
        });
      } catch (_) {
        // Shopify's native values remain available when saved values cannot be restored.
      }
    };

    const clearValues = () => {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (_) {
        // Nothing else is required when storage is unavailable.
      }
    };

    const setPartnershipType = (sourceType) => {
      if (!sourceType) return;
      const select = form.querySelector('[name="contact[partnership_type]"]');
      const options = select ? [...select.options] : [];
      const matchingOption = options.find((option) => option.value.toLowerCase() === sourceType.toLowerCase());
      if (matchingOption) select.value = matchingOption.value;
    };

    const openDialog = (trigger) => {
      if (dialog.open) return;
      dialog.__returnFocus = trigger || null;
      setPartnershipType(trigger?.dataset.partnershipType);
      document.body.classList.add('cy-partnership-inquiry--locked');
      dialog.showModal();
    };

    dialog.__openPartnershipInquiry = openDialog;

    openButtons.forEach((button) => {
      button.dataset.partnershipTriggerInitialized = 'true';
      button.addEventListener('click', () => openDialog(button));
    });

    dialog.addEventListener('close', () => {
      if (!document.querySelector('[data-partnership-dialog][open]')) {
        document.body.classList.remove('cy-partnership-inquiry--locked');
      }
    });

    form.addEventListener('input', saveValues);
    form.addEventListener('change', saveValues);
    form.addEventListener('submit', () => {
      if (!form.checkValidity() || !submitButton) return;
      form.classList.add('is-submitting');
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    });

    if (dialog.hasAttribute('data-partnership-submitted')) clearValues();
    else restoreValues();
    if (dialog.hasAttribute('data-partnership-open-on-load')) openDialog(null);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeModals();
  initializePartnershipInquiry();
});
document.addEventListener('shopify:section:load', (event) => {
  initializeModals(event.target);
  initializePartnershipInquiry(document);
});
